import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { AuthService } from '@/shared/services/auth';
import type { components } from '@/core/types/generated/api';
import { jwtDecode } from 'jwt-decode';
import { normalizeRoleToString } from '@/shared/utils/roleUtils';
import type { UserAuth } from '@/core/types/models/UserAuth';
import {
  isApiErrorResponse,
  isProblemDetails,
  isValidationErrorResponse,
  type ValidationErrorResponse,
} from '@/shared/services/http-helpers';
export const useAuthStore = defineStore('auth', () => {
  // Production defaults: no tokens, no user
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const user = ref<UserAuth | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  function setToken(token: string, refresh: string) {
    accessToken.value = token;
    refreshToken.value = refresh;
    // derive expiresAt from JWT exp claim if available
    const exp = parseJwtExp(token);
    if (user.value) {
      (user.value as any).expiresAt = exp || (user.value as any).expiresAt || new Date(Date.now() + 1000 * 60 * 60);
    }
  }

  function clearToken() {
    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
  }

  // Parse JWT exp claim using jwt-decode (no signature verification)
  function parseJwtExp(token: string | null): Date | null {
    if (!token) return null;
    try {
      const payload = jwtDecode<Record<string, unknown>>(token);
      const exp = payload?.exp;
      if (typeof exp === 'number') return new Date(exp * 1000);
    } catch {
      // ignore parse errors
    }
    return null;
  }

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;
    try {
      const response = await AuthService.login(email, password);
  const payloadError = extractPayloadError(response);
      if (payloadError) {
        error.value = payloadError;
        return false;
      }

      if (response && response.accessToken && response.refreshToken && response.id && response.systemRole) {
        accessToken.value = response.accessToken;
        refreshToken.value = response.refreshToken;
        user.value = {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          id: response.id,
          systemRole: response.systemRole,
          expiresAt: response.expiresAt ? new Date(response.expiresAt) : new Date(Date.now() + 24 * 60 * 60 * 1000) // Default to 24h if not provided
        };
        return true;
      }

      error.value = 'Invalid login response.';
      return false;
    } catch (cause: unknown) {
      error.value = resolveAuthErrorMessage(cause);
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function refresh() {
    if (!refreshToken.value) return false;
    loading.value = true;
    error.value = null;
    try {
      const response = await AuthService.refreshToken(refreshToken.value);
      if (response && response.accessToken && response.refreshToken && response.id && response.systemRole) {
        accessToken.value = response.accessToken;
        refreshToken.value = response.refreshToken;
        user.value = {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          id: response.id,
          systemRole: response.systemRole,
          expiresAt: response.expiresAt ? new Date(response.expiresAt) : new Date(Date.now() + 24 * 60 * 60 * 1000) // Default to 24h if not provided
        };
        return true;
      } else {
        error.value = 'Invalid refresh response.';
        return false;
      }
    } catch (cause: unknown) {
      error.value = resolveAuthErrorMessage(cause, 'Token refresh failed.');
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    loading.value = true;
    error.value = null;
    try {
      await AuthService.logout();
    } catch (e: any) {
      // Ignore error, proceed with logout
    } finally {
      accessToken.value = null;
      refreshToken.value = null;
      user.value = null;
      loading.value = false;
    }
  }

  async function fetchCurrentUser(includeProjectMemberships = false) {
    loading.value = true;
    error.value = null;
    try {
      const data = await AuthService.getCurrentUser(includeProjectMemberships);
      if (data) {
        // Map returned UserDto into the auth user shape
        // Map system role to SystemRoleType
        const roleValue = (data as any).role as string | number | undefined;
        // Normalize role value to string format
        const systemRole = normalizeRoleToString(roleValue) || 'User';

        user.value = {
          id: data.id,
          accessToken: accessToken.value || '',
          refreshToken: refreshToken.value || '',
          systemRole,
          expiresAt: parseJwtExp(accessToken.value) || new Date(Date.now() + 1000 * 60 * 60),
        } as UserAuth;
        // Attach optional profile fields to user auth store if available
        (user.value as any).email = (data as any).email;
        (user.value as any).fullName = (data as any).fullName;
      }
      return data as components['schemas']['UserDto'] | null;
    } catch (cause: unknown) {
      error.value = resolveAuthErrorMessage(cause, 'Failed to fetch user.');
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    loading.value = true;
    error.value = null;
    try {
      const success = await AuthService.changePassword(currentPassword, newPassword);
      if (success) {
        return true;
      } else {
        error.value = 'Failed to change password';
        return false;
      }
    } catch (cause: unknown) {
      error.value = resolveAuthErrorMessage(cause, 'Failed to change password');
      return false;
    } finally {
      loading.value = false;
    }
  }

  return {
    accessToken,
    refreshToken,
    user,
    loading,
    error,
    setToken,
    clearToken,
    login,
    refresh,
    logout,
    fetchCurrentUser,
    changePassword,
  };
}, {
  persist: {
    // Exclude loading and error states from persistence
    // This prevents stale loading/error states from previous sessions
    omit: ['loading', 'error']
  },
});

  function extractPayloadError(response: unknown): string | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const record = response as Record<string, unknown>;
    const candidate = record.error ?? record.message;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }

    return null;
  }

  function resolveAuthErrorMessage(cause: unknown, fallback = 'Login failed.'): string {
    if (isAxiosError(cause)) {
      const data = cause.response?.data as unknown;

      if (isValidationErrorResponse(data)) {
        const validationMessage = extractFirstValidationMessage(data);
        if (validationMessage) {
          return validationMessage;
        }
        return data.title ?? fallback;
      }

      if (isApiErrorResponse(data)) {
        return data.detail ?? data.title ?? fallback;
      }

      if (isProblemDetails(data)) {
        return data.detail ?? data.title ?? fallback;
      }

      const plainMessage = extractPlainMessage(data);
      if (plainMessage) {
        return plainMessage;
      }

      if (typeof cause.message === 'string' && cause.message.trim()) {
        return cause.message.trim();
      }
    }

    if (cause instanceof Error && typeof cause.message === 'string' && cause.message.trim()) {
      return cause.message.trim();
    }

    return fallback;
  }

  function extractPlainMessage(data: unknown): string | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const record = data as Record<string, unknown>;
    const candidates = [record.error, record.message, record.detail];
    const match = candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0);
    return match ? match.trim() : null;
  }

  function extractFirstValidationMessage(data: ValidationErrorResponse): string | null {
    const errorEntries = Object.values(data.errors ?? {});
    for (const entry of errorEntries) {
      if (Array.isArray(entry)) {
        const message = entry.find((item): item is string => typeof item === 'string' && item.trim().length > 0);
        if (message) {
          return message.trim();
        }
      }
    }
    return null;
  }
